import { useState, useEffect, useCallback } from 'react';
import isNull from 'lodash/isNull';
import isNil from 'lodash/isNil';

import type { Nullable } from 'chaika-common-types';
import type { UseDurationFormData, UseDurationFormState, UseDurationFormParams } from './types';

import { translate } from '@chaika/i18n/helper';
import { isDefined } from '@utils/isDefined';
import { DateHelper } from '@utils/DateHelper';
import { assertIsDefined } from '@utils/assertIsDefined';
import { Declension } from '@utils/Declension';

import { OptionType } from '../../Select';
import { TimeData } from '@components/TimeSelect';
import { TimeSelectName } from '@components/DurationForm/DurationForm';

const INDEFINITE_DURATION = 'indefinite';

export function useDurationForm({
    name,
    startDate,
    disableInfinityDuration,
    onDateChange,
    ...params
}: UseDurationFormParams): UseDurationFormData {
    const tIndefinite = translate('indefinite');

    const [state, setState] = useState<UseDurationFormState>({
        duration: params.duration ?? INDEFINITE_DURATION,
        endDate: null,
        durationOptions:
            disableInfinityDuration || params.durationOptions?.length === 0
                ? params.durationOptions || []
                : [
                      {
                          value: INDEFINITE_DURATION,
                          label: tIndefinite,
                      },
                      ...(params.durationOptions || []),
                  ],
    });

    const updateState = useCallback(({ duration, endDate }: Omit<UseDurationFormState, 'durationOptions'>): void => {
        setState(prev => {
            const durationOptions = [...prev.durationOptions];
            if (!durationOptions.some(({ value }) => value === duration)) {
                durationOptions.push({
                    value: duration,
                    label: `${duration} ${Declension.days(duration)}`,
                });
            }
            return { ...prev, endDate, duration, durationOptions };
        });
    }, []);

    useEffect(() => {
        const { endDate, duration } = calculateStateParams(
            { endDate: params.endDate, duration: params.duration },
            startDate,
        );
        updateState({ endDate, duration });
    }, [params.endDate, params.duration, startDate, updateState]);

    function onDurationChange({ value: duration }: OptionType<string | number>): void {
        // @ts-ignore
        const { endDate } = calculateStateParams({ duration }, startDate);
        updateState({ endDate, duration });
        onDateChange({ endDate }, duration === INDEFINITE_DURATION ? null : (duration as number), name);
    }

    function onStartDateChange(date?: Nullable<Date>, dateInputName?: string): void {
        const startDate = !!date ? DateHelper.getStartOfDay(date) : null;

        if (!startDate) {
            onDateChange({ startDate, endDate: state.endDate }, state.duration as number, name ?? dateInputName);
        } else if (!!state.endDate && DateHelper.isAfter(startDate, state.endDate)) {
            const { endDate, duration } = calculateStateParams(
                { endDate: DateHelper.getEndOfDay(startDate) },
                startDate,
            );
            updateState({ endDate, duration });
            onDateChange({ startDate, endDate }, duration as number, name ?? dateInputName);
        } else {
            const duration = calculateDuration(state.endDate, startDate);
            updateState({ duration, endDate: state.endDate });
            onDateChange(
                { startDate },
                duration === INDEFINITE_DURATION ? null : (duration as number),
                name ?? dateInputName,
            );
        }
    }

    function onEndDateChange(date: Nullable<Date>, dateInputName?: string): void {
        const { endDate, duration } = calculateStateParams(
            { endDate: !!date ? DateHelper.getEndOfDay(date) : date },
            startDate,
        );
        updateState({ endDate, duration });
        onDateChange({ endDate }, duration as number, name ?? dateInputName);
    }

    function onTimeChange(time: TimeData, name?: TimeSelectName) {
        if (name === TimeSelectName.Start) {
            assertIsDefined(startDate, 'Start time is changed only for a set start date');
            const date = DateHelper.setTimeData(startDate, time);
            onDateChange({
                startDate: params.endDate && date > params.endDate ? params.endDate : date,
                endDate: params.endDate,
            });
        } else {
            assertIsDefined(params.endDate, 'End time is changed only for a set end date');
            const date = DateHelper.setTimeData(params.endDate, time);
            onDateChange({ endDate: startDate && date > startDate ? date : startDate, startDate });
        }
    }

    return {
        ...state,
        onTimeChange,
        onDurationChange,
        onStartDateChange,
        onEndDateChange,
    };
}

function calculateStateParams(
    params: Pick<UseDurationFormParams, 'duration' | 'endDate'>,
    startDate?: Nullable<Date>,
): Omit<UseDurationFormState, 'durationOptions'> {
    let endDate: Nullable<Date>;
    let duration: number | string;
    if (isDefined(params.endDate)) {
        endDate = params.endDate;
        duration = calculateDuration(endDate, startDate);
    } else {
        duration = params.duration ?? INDEFINITE_DURATION;
        endDate = calculateEndDate(duration, startDate);
    }

    return { endDate, duration };
}

function calculateDuration(endDate: Nullable<Date>, startDate?: Nullable<Date>): string | number {
    return isDefined(startDate) && !isNil(endDate)
        ? isNull(startDate)
            ? 0
            : DateHelper.differenceInCalendarDays(DateHelper.addDays(endDate, 1), startDate)
        : INDEFINITE_DURATION;
}

function calculateEndDate(duration: string | number, startDate?: Nullable<Date>): Nullable<Date> {
    return duration !== INDEFINITE_DURATION && !isNil(startDate)
        ? DateHelper.getEndOfDay(DateHelper.addDays(startDate, (duration as number) - 1))
        : null;
}
